import {
  Button,
  Chip,
  Grid,
  Container,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import React, {useEffect, useState} from 'react';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ContentTypeSwitch from './ContentTypeSwitch';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {useAppDispatch, useAppSelector} from '../../../../store/hooks';
import {actionCreators} from '../../../../store/poctoolsContentManagement/poctoolsContentManagementActions';
import {validateContent} from '../../../../Util/validators';
import NewItemsList from './NewItemsList';
import {ApplicationState} from '../../../../store';
import {useNavigate} from 'react-router-dom';
import {editPOCTool} from '../helper/editPOCTool';
import Loader from '../../../POCWebPortal/generalPurpose/Loader';
import HeaderBar from '../helper/POCToolCreatorHeaderBar';

type Props = {
  goBackStage: Function;
  goNextStage: Function;
  setIsPOCToolChanged: Function;
  isPOCToolChanged: boolean;
};

export default function ContentBuilder(props: Props) {
  const {goBackStage, goNextStage, setIsPOCToolChanged, isPOCToolChanged} =
    props;

  const [displayPocToolContent, setDisplayPocToolContent] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const content = useAppSelector(
    state => state.poctoolsContentManagementState.displayPocToolContent,
  );
  // displayPocToolContent is a sorted array containing our content, making it easy to manage, reorder, and manipulate. It consists solely of content without any information about the POCTool.

  const pocToolSummary = useAppSelector(
    (state: ApplicationState) =>
      state.poctoolsContentManagementState.pointOfCareTool,
  );
  // We use general information about the POCTool from pocToolSummary, such as name, description, image, and more.

  // The handleDrag functions manage the change in order within the displayPocToolContent array by allowing users to drag and drop elements.
  const handleDragStart = (event, index) => {
    setDraggedItem(displayPocToolContent[index]);
    event.dataTransfer.setDragImage(event.target.parentNode, 20, 20);
  };
  const handleDragOver = index => {
    const draggedOverItem = displayPocToolContent[index];
    if (draggedOverItem === draggedItem || !draggedItem) {
      return;
    }
    const items = displayPocToolContent.filter(item => item !== draggedItem);
    if (!draggedOverItem) {
      items.push(draggedItem);
    } else {
      const draggedIndex = items.indexOf(draggedOverItem);
      items.splice(draggedIndex, 0, draggedItem);
    }
    setDisplayPocToolContent(items);
    setIsPOCToolChanged(true);
  };
  const handleDragEnd = () => {
    setDraggedItem(null);
  };
  const removeItem = id => {
    setDisplayPocToolContent(
      displayPocToolContent.filter(item => item.id !== id),
    );
    setIsPOCToolChanged(true);
  };

  const handleSave = async event => {
    event.preventDefault();
    try {
      return editPOCTool({
        pocToolSummary,
        displayPocToolContent,
        id: pocToolSummary.id,
        setIsLoading,
        setIsSubmitted,
        navigate,
      }).then(POCToolCreatedId => {
        dispatch(actionCreators.setPOCToolContent(displayPocToolContent));
        setTimeout(() => {
          setIsSubmitted(false);
          return navigate(`/EditPOCTool/:${POCToolCreatedId}`);
        }, 2000);
        setIsPOCToolChanged(false);
      });
    } catch (err) {
      dispatch(actionCreators.setPOCToolContent(displayPocToolContent));
      if (err === 401) {
        navigate('/Login');
        throw err;
      }
    }
  };

  const handleContinue = event => {
    const isValid = validateContent(displayPocToolContent);
    if (isValid && isPOCToolChanged) {
      handleSave(event).then(() => goNextStage());
    } else {
      goNextStage();
    }
  };
  const handleSetDisplayPocToolContent = content => {
    setIsPOCToolChanged(true);
    setDisplayPocToolContent(content);
  };

  useEffect(() => {
    setDisplayPocToolContent(content);
    //we are setting content from store into state to avoid getting every update from store
  }, []);
  useEffect(() => {
    dispatch(actionCreators.setPOCToolContent(displayPocToolContent));
  }, [displayPocToolContent]);

  return isLoading ? (
    <Container maxWidth="md">
      <Loader />
    </Container>
  ) : isSubmitted ? (
    <Container style={{marginTop: '25%'}} maxWidth="md">
      <Typography variant="h4" align="center">
        Your Point-of-Care Tool is saved!
      </Typography>
    </Container>
  ) : (
    <Box style={styles.container} width={'100%'}>
      <Box paddingBottom={2}>
        <HeaderBar title={'Content Builder'} />
      </Box>

      {displayPocToolContent.map((item, index) => (
        <Grid
          key={item.id}
          onDragOver={() => handleDragOver(index)}
          onDragEnd={handleDragEnd}
          style={styles.contentContainer}>
          <Grid draggable onDragStart={event => handleDragStart(event, index)}>
            <DragIndicatorIcon style={{cursor: 'grab'}} />
          </Grid>
          <Grid style={styles.chipAndContentTypeSwitchContainer}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 10,
              }}>
              <Box paddingRight={2}>
                <span style={styles.numbering}>{index + 1}</span>
              </Box>
              <Chip
                style={{
                  backgroundColor: item.backgroundColor,
                  color: item.color,
                }}
                label={item.title}
              />
              {(item.type === 'paragraph' ||
                item.type === 'video' ||
                item.type === 'pdf' ||
                item.type === 'img') && (
                <FormControl size="small" style={{marginLeft: 10}}>
                  <InputLabel id="demo-simple-select-label">
                    Attach to
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={item.attachTo}
                    label="Attach to"
                    onChange={value => {
                      setDisplayPocToolContent(
                        displayPocToolContent.map(itemToChange =>
                          item.id === itemToChange.id
                            ? {...itemToChange, attachTo: value.target.value}
                            : itemToChange,
                        ),
                      );
                    }}>
                    <MenuItem value={'lastParent'}>Last parent</MenuItem>
                    <MenuItem value={'lastTopic'}>Last topic</MenuItem>
                    <MenuItem value={'lastSubtopic'}>Last subtopic</MenuItem>
                  </Select>
                </FormControl>
              )}
            </div>
            {
              <ContentTypeSwitch
                item={item}
                index={index}
                displayPocToolContent={displayPocToolContent}
                setDisplayPocToolContent={handleSetDisplayPocToolContent}
              />
            }
          </Grid>
          <Grid onClick={() => removeItem(item.id)}>
            <HighlightOffIcon style={styles.highlightOffIcon} color="warning" />
          </Grid>
        </Grid>
      ))}
      <Grid
        style={styles.lastDropZone}
        onDragOver={() => handleDragOver(displayPocToolContent.length)}></Grid>
      <NewItemsList
        setDisplayPocToolContent={handleSetDisplayPocToolContent}
        displayPocToolContent={displayPocToolContent}
      />
      <Grid style={styles.continueButton}>
        <Button
          variant="contained"
          style={styles.saveButton}
          onClick={() => goBackStage()}>
          Back to Settings
        </Button>
        <Box>
          <Button
            variant="contained"
            style={styles.saveButton}
            onClick={handleSave}>
            Save
          </Button>
          <Button variant="contained" onClick={handleContinue}>
            Save and Continue
          </Button>
        </Box>
      </Grid>
    </Box>
  );
}

const styles = {
  container: {
    display: 'flex',
    paddingTop: 10,
    flexDirection: 'column',
  },
  saveButton: {marginRight: 20},
  chipContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },

  chipAndContentTypeSwitchContainer: {
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #1976D2',
    padding: 10,
    marginBottom: 10,
    flex: 1,
  },
  contentContainer: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
  },
  lastDropZone: {
    height: 50,
  },
  topicButton: {
    color: '#0C7CFF',
    width: '100%',
  },
  list: {
    width: '100%',
    maxWidth: 360,
    flexDirection: 'column',
    border: '1px solid #1976D2',
    borderTop: 0,
  },
  highlightOffIcon: {cursor: 'pointer'},
  continueButton: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: 50,
  },
  numbering: {fontWeight: 'bold', color: '#708090'},
};
